/**
 * How a tab answers the OIDC check-session poll, shared by the three Branduals SPAs.
 *
 * The auth server's check-session frame hashes the session cookie it can read and compares it
 * with the session_state the tab received at sign-in. A mismatch, or a cookie it cannot read at
 * all, comes back as "changed", and the apps answer that by re-asking the server who is signed in
 * (clear local state, promptless authorize). That answer is right when someone signed in or out
 * in another tab.
 *
 * It loops when the frame can never read the cookie. Some browsers block storage for an embedded
 * auth-server frame even on the same site, and the frame then reports "changed" on every poll. The
 * re-check comes back as the same person, the next poll says "changed" again, and the tab reloads
 * itself without end. The authorize request is a top-level navigation and still carries the
 * cookie, which is why the server keeps answering.
 *
 * The guard: before a re-check, remember who was signed in. If "changed" arrives again and the
 * same person is still signed in, the frame cannot see this browser's session, so the tab stops
 * reacting to it. Such a tab learns of a sign-out elsewhere at its next token renewal instead. The
 * first "unchanged" proves the frame works and clears the record, so a browser whose frame can
 * read the cookie keeps the prompt cross-tab detection.
 *
 * The record lives in tab-local sessionStorage so it survives the re-check's redirect, under its
 * own prefix: the token storage's clear() removes every `branduals-auth.` key, and the apps call
 * logoffLocal() right after recording.
 */

const RECHECKED_FOR_KEY = 'branduals-check-session.recheckedFor';

/** What the app does with one check-session answer. */
export type CheckSessionReaction = 'recheck' | 'ignore';

/**
 * Decides one check-session answer. `signedInSubject` is the `sub` of the person this tab holds
 * tokens for, or null when it holds none. 'recheck' means: run the app's quiet re-check now.
 */
export function reactToCheckSession(
  value: unknown,
  signedInSubject: string | null | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = sessionStorage,
): CheckSessionReaction {
  if (value === 'unchanged') {
    storage.removeItem(RECHECKED_FOR_KEY);
    return 'ignore';
  }

  if (signedInSubject && storage.getItem(RECHECKED_FOR_KEY) === signedInSubject) {
    return 'ignore';
  }

  if (signedInSubject) {
    storage.setItem(RECHECKED_FOR_KEY, signedInSubject);
  } else {
    storage.removeItem(RECHECKED_FOR_KEY);
  }
  return 'recheck';
}
